'use client';

import * as React from 'react';
import Link from 'next/link';
import { useStats } from '@/hooks/useStats';
import { TrendingUp, Zap } from 'lucide-react';
import { TopPatternRowSkeleton } from './StatCardSkeleton';

const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num);

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-3">
    {[...Array(5)].map((_, i) => (
      <TopPatternRowSkeleton key={i} />
    ))}
  </div>
);

const EmptyState: React.FC = () => (
  <div className="text-center py-12">
    <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-3" />
    <p className="text-gray-400">No patterns found</p>
  </div>
);

interface TopPattern {
  id: string;
  name: string;
  category: string;
  uses: number;
  tokens_saved: number;
}

export function TopPatterns() {
  const { stats, isLoading } = useStats();

  // Memoize topPatterns to avoid unnecessary re-renders
  const topPatterns = React.useMemo(() => {
    return (stats?.topPatterns || []) as TopPattern[];
  }, [stats]);

  // Memoize the sliced patterns list to avoid unnecessary array operations
  const displayPatterns = React.useMemo(() => {
    return topPatterns.slice(0, 5);
  }, [topPatterns]);

  if (isLoading) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Top Patterns</h2>
        <LoadingSkeleton />
      </div>
    );
  }

  if (!topPatterns || topPatterns.length === 0) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Top Patterns</h2>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      <h2 className="text-xl font-bold text-gray-100 mb-4">Top Patterns</h2>

      <div className="space-y-3">
        {displayPatterns.map((pattern, index) => (
          <Link
            key={pattern.id}
            href={`/patterns/${pattern.id}`}
            className="block bg-gray-700/30 hover:bg-gray-700/50 rounded-lg p-4 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center font-bold text-white text-sm">
                #{index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-100 truncate">{pattern.name}</h3>
                  <span className="text-xs px-2 py-0.5 bg-gray-700 rounded text-gray-300 flex-shrink-0">
                    {pattern.category}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    {formatNumber(pattern.uses)} uses
                  </span>
                  <span className="flex items-center gap-1">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    {formatNumber(pattern.tokens_saved)} tokens
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
